import { generateTimeOptions, DaysOfWeekInOrder, compareLabels } from '@helpers/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { CityDto, ZoneDto } from '@interfaces/types';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DayOfWeek } from '@helpers/enums';
import { useForm } from 'react-hook-form';
import { TFunction } from 'i18next';
import { z } from 'zod';

import { useServicesFormatter } from '../../hooks';

export const publicTransportConfigurationFormDefaultValues = {
  unlimitedTime: true,
  timeFrom: '08',
  timeTo: '20',
  cities: [],
  zones: [],
  days: [],
};

const publicTransportConfigurationFormSchema = (t: TFunction) =>
  z
    .object({
      cities: z
        .array(z.string())
        .min(1, t('COMPANY_POLICY.PUBLIC_TRANSPORT_CONFIGURATION_CITIES_REQUIRED')),
      zones: z.array(z.string()),
      days: z
        .array(z.nativeEnum(DayOfWeek))
        .min(1, t('COMPANY_POLICY.PUBLIC_TRANSPORT_CONFIGURATION_DAYS_REQUIRED')),
      timeFrom: z.string().optional().nullable(),
      timeTo: z.string().optional().nullable(),
      unlimitedTime: z.boolean({ coerce: true }),
    })
    .superRefine((data, ctx) => {
      if (!data.unlimitedTime) {
        if (!data.timeFrom) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('FORMS.REQUIRED'),
            path: ['timeFrom'],
          });
        }

        if (!data.timeTo) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('FORMS.REQUIRED'),
            path: ['timeTo'],
          });
        }

        if (data.timeFrom && data.timeTo && data.timeFrom >= data.timeTo) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('COMPANY_POLICY.PUBLIC_TRANSPORT_CONFIGURATION_TIME_INVALID'),
            path: ['timeTo'],
          });
        }
      }
    });

export type PublicTransportConfigurationFormData = z.infer<
  ReturnType<typeof publicTransportConfigurationFormSchema>
>;

interface UsePublicTransportConfigurationFormProps {
  defaultValues: PublicTransportConfigurationFormData;
  days: DayOfWeek[];
  cities: CityDto[];
  zones: ZoneDto[];
}

export const usePublicTransportConfigurationForm = (
  props: UsePublicTransportConfigurationFormProps,
) => {
  const { defaultValues, cities, days, zones } = props;

  const { formatCities, formatDays, formatZones } = useServicesFormatter();
  const { t } = useTranslation();

  const form = useForm({
    resolver: zodResolver(publicTransportConfigurationFormSchema(t)),
    defaultValues: defaultValues ?? publicTransportConfigurationFormDefaultValues,
    mode: 'onBlur',
  });

  const unlimitedTimeSelected = form.watch('unlimitedTime');
  const formSelectedTimeFrom = form.watch('timeFrom');
  const formSelectedCities = form.watch('cities');
  const formSelectedTimeTo = form.watch('timeTo');
  const formSelectedZones = form.watch('zones');
  const formSelectedDays = form.watch('days');

  const selectedCitiesCustomValue = formatCities(formSelectedCities, cities);
  const selectedDaysCustomValue = formatDays(formSelectedDays as DayOfWeek[]);
  const selectedZonesCustomValue = formatZones(
    formSelectedCities,
    formSelectedZones,
    cities,
    zones,
  );

  const [selectedCitiesIds, setSelectedCitiesIds] = useState<string[]>(formSelectedCities);

  const citiesOptions = useMemo(() => {
    return cities.map((city) => ({
      label: city.name,
      value: city.code,
    }));
  }, [cities]);

  const daysOptions = useMemo(() => {
    return days.map((day) => ({
      label: t(`DAYS.${day}`),
      value: day,
    }));
  }, [days, t]);

  const selectedCitiesOptions = useMemo(() => {
    return citiesOptions.filter(({ value }) => formSelectedCities.includes(value));
  }, [citiesOptions, formSelectedCities]);

  const zonesOptions = useMemo(() => {
    return zones
      .filter(({ cityCode }) => formSelectedCities.includes(cityCode))
      .map((zone) => ({
        node: zone.cityCode,
        label: zone.name,
        value: zone.code,
        type: zone.type,
      }));
  }, [formSelectedCities, zones]);

  const timeFromOptions = useMemo(
    () =>
      generateTimeOptions().map((option) => ({
        ...option,
        disabled: formSelectedTimeTo ? option.value >= formSelectedTimeTo : false,
      })),
    [formSelectedTimeTo],
  );

  const timeToOptions = useMemo(
    () =>
      generateTimeOptions().map((option) => ({
        ...option,
        disabled: formSelectedTimeFrom ? option.value <= formSelectedTimeFrom : false,
      })),
    [formSelectedTimeFrom],
  );

  const sortCities = (selectedValues: string[]) => {
    return selectedValues.sort((a, b) => {
      const cityA = citiesOptions.find((city) => city.value === a);
      const cityB = citiesOptions.find((city) => city.value === b);

      if (cityA && cityB) {
        return compareLabels(cityA, cityB);
      }
      return 0;
    });
  };

  const sortDays = (selectedValues: string[]) => {
    return selectedValues.sort(
      (a, b) =>
        DaysOfWeekInOrder.indexOf(a as DayOfWeek) - DaysOfWeekInOrder.indexOf(b as DayOfWeek),
    );
  };

  // This is for manipulating selected zones in case when selected cities are changed
  useEffect(() => {
    const addedCities = formSelectedCities.filter((city) => !selectedCitiesIds.includes(city));
    const zonesToRemove = zones
      .filter(({ cityCode }) => !formSelectedCities.includes(cityCode))
      .map(({ code }) => code);
    const zonesToAdd = zones
      .filter(({ cityCode }) => addedCities.includes(cityCode))
      .map(({ code }) => code);
    const selectedZones = new Set([
      ...formSelectedZones.filter((zone) => !zonesToRemove.includes(zone)),
      ...zonesToAdd,
    ]);
    form.setValue('zones', Array.from(selectedZones));
    setSelectedCitiesIds(formSelectedCities);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSelectedCities, zones, form]);

  useEffect(() => {
    if (unlimitedTimeSelected) {
      form.setValue('timeFrom', null);
      form.setValue('timeTo', null);
    } else if (!formSelectedTimeFrom) {
      form.setValue('timeFrom', publicTransportConfigurationFormDefaultValues.timeFrom);
      form.setValue('timeTo', publicTransportConfigurationFormDefaultValues.timeTo);
    }
  }, [unlimitedTimeSelected, formSelectedTimeFrom, form]);

  return {
    selectedCitiesCustomValue,
    selectedZonesCustomValue,
    selectedDaysCustomValue,
    unlimitedTimeSelected,
    selectedCitiesOptions,
    timeFromOptions,
    timeToOptions,
    citiesOptions,
    zonesOptions,
    daysOptions,
    sortCities,
    sortDays,
    form,
  };
};
